import { z } from 'zod';
import { siteDomain } from './site.ts';

/**
 * Access to the private part of a site (behind a sign-in), given by the project's owner with a test account.
 * Two ways: the account's email and password (the runner signs in on the site's own form), or the cookies of a
 * browser already signed in (the runner starts the visit with them). It belongs to the project, so to one site,
 * and it is used only in that project's visits. The model never sees the secrets.
 */
export const ACCESS_KINDS = ['credentials', 'cookies'] as const;
export type AccessKind = (typeof ACCESS_KINDS)[number];

/**
 * Where a saved access stands: `ready` until a visit could not use it; `failed` (the sign-in did not get past
 * the form: wrong password, two-factor) and `expired` (the cookies no longer sign in) ask for a new one.
 */
export const ACCESS_STATUSES = ['ready', 'failed', 'expired'] as const;
export type AccessStatus = (typeof ACCESS_STATUSES)[number];

/**
 * Whether a test uses access: `auto` uses the project's saved access, or asks the person when a visitor meets a
 * sign-in page without one; `none` never signs in and never asks.
 */
export const TEST_ACCESS_MODES = ['auto', 'none'] as const;
export type TestAccessMode = (typeof TEST_ACCESS_MODES)[number];

/**
 * A test's question to the person, one per test whatever the number of visitors that meet a sign-in page:
 * `pending` (visitors wait), `declined` (go on without signing in), `provided` (an access was saved for it).
 */
export const ACCESS_REQUEST_STATES = ['pending', 'declined', 'provided'] as const;
export type AccessRequestState = (typeof ACCESS_REQUEST_STATES)[number];

/** How long a visitor waits at a sign-in page for the person's answer, in total over the visit. */
export const ACCESS_WAIT_MAX_MS = 5 * 60 * 1000;
/** Version of the confirmation text saved with an access ("this is a test account I may use"). */
export const ACCESS_CONSENT_VERSION = 'access-2026-10-24';

/**
 * Authentication services that sites call from the browser: during the sign-in they receive the form, and
 * afterwards the session is refreshed there (Clerk renews its token every minute). Their cookies are accepted
 * with the site's.
 */
export const AUTH_SERVICE_HOSTS = [
  'clerk.accounts.dev',
  'accounts.dev',
  'clerk.com',
  'auth0.com',
  'supabase.co',
  'identitytoolkit.googleapis.com',
  'securetoken.googleapis.com',
  'kinde.com',
  'stytch.com',
] as const;

const hostMatches = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

export function isAuthServiceHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\./, '');
  return AUTH_SERVICE_HOSTS.some((d) => hostMatches(host, d));
}

/** First labels of a site's own subdomains that serve sign-in and sessions (`clerk.pullo.io`, `auth.pullo.io`). */
const AUTH_LABELS = new Set(['clerk', 'auth', 'accounts', 'account', 'login', 'id', 'identity', 'sso', 'session', 'sessions']);

/**
 * A host where a signed-in visit may keep sending session requests (token refresh): an authentication service,
 * or a subdomain of the site that serves sign-in. Never the site's own pages: there a POST is a form, and forms
 * stay intercepted.
 */
export function isSessionHost(hostname: string, siteUrl: string): boolean {
  const host = hostname.toLowerCase();
  if (isAuthServiceHost(host)) return true;
  const site = siteDomain(siteUrl);
  if (!site || !hostMatches(host, site) || host === site) return false;
  const label = host.slice(0, -site.length - 1).split('.')[0]!;
  return AUTH_LABELS.has(label);
}

/** Requests that change or close the account rather than keep its session: never let through. */
const DESTRUCTIVE_PATH = /\b(delete|destroy|remove|deactivate|password|email_addresses|phone_numbers|passkeys|totp|backup_codes|organizations|memberships|invitations|billing|subscription|payment)\b/i;

export function isDestructiveAuthPath(pathname: string): boolean {
  return DESTRUCTIVE_PATH.test(pathname);
}

/** A cookie as the runner sets it (Playwright's shape). `expires`: seconds since 1970, -1 for a session cookie. */
export interface AccessCookie {
  name: string;
  value: string;
  domain: string;
  path: string;
  expires: number;
  httpOnly: boolean;
  secure: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/** What the person sees of saved cookies: never their values. */
export interface AccessCookieSummary {
  name: string;
  domain: string;
  /** ISO date; null for a session cookie. */
  expiresAt: string | null;
}

export interface RejectedCookie {
  name: string;
  domain: string;
  reason: 'other_site' | 'expired' | 'invalid';
}

export interface CookieImport {
  accepted: AccessCookie[];
  rejected: RejectedCookie[];
}

const MAX_COOKIES = 100;
const MAX_VALUE = 8192;

function sameSiteOf(raw: unknown): AccessCookie['sameSite'] {
  const v = typeof raw === 'string' ? raw.toLowerCase() : '';
  if (v === 'strict') return 'Strict';
  if (v === 'lax') return 'Lax';
  if (v === 'none' || v === 'no_restriction') return 'None';
  return undefined;
}

function num(raw: unknown): number | null {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/** One cookie object from a browser extension export (Cookie-Editor, EditThisCookie), a Playwright storage state or a HAR. */
function fromObject(o: Record<string, unknown>): AccessCookie | null {
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  const value = typeof o.value === 'string' ? o.value : '';
  const domain = typeof o.domain === 'string' ? o.domain.trim().toLowerCase() : typeof o.host === 'string' ? o.host.trim().toLowerCase() : '';
  if (!name || !domain || value.length > MAX_VALUE || /[;\s]/.test(name)) return null;
  const session = o.session === true;
  const expiry = num(o.expires) ?? num(o.expirationDate) ?? num(o.expiry);
  // Some exports give milliseconds; dates far beyond 2100 in seconds are milliseconds.
  const seconds = expiry === null || session || expiry <= 0 ? -1 : expiry > 4_102_444_800 ? Math.round(expiry / 1000) : Math.round(expiry);
  return {
    name,
    value,
    domain,
    path: typeof o.path === 'string' && o.path.startsWith('/') ? o.path : '/',
    expires: seconds,
    httpOnly: o.httpOnly === true,
    secure: o.secure === true,
    sameSite: sameSiteOf(o.sameSite),
  };
}

/** Netscape cookies.txt: domain, subdomains flag, path, secure, expiry, name, value (tab separated). */
function fromNetscape(text: string): AccessCookie[] {
  const out: AccessCookie[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const httpOnly = raw.startsWith('#HttpOnly_');
    const line = httpOnly ? raw.slice('#HttpOnly_'.length) : raw;
    if (!line.trim() || line.startsWith('#')) continue;
    const parts = line.split('\t');
    if (parts.length < 7) continue;
    const [domain, , path, secure, expires, name, ...value] = parts;
    const c = fromObject({ domain, path, secure: secure?.toUpperCase() === 'TRUE', expires: Number(expires), name, value: value.join('\t'), httpOnly });
    if (c) out.push(c);
  }
  return out;
}

/** `name=value; name2=value2`, as a browser's Cookie header shows it: cookies of the site's own host. */
function fromHeader(text: string, siteUrl: string): AccessCookie[] {
  let host: string;
  try {
    host = new URL(siteUrl).hostname.toLowerCase();
  } catch {
    return [];
  }
  return text
    .replace(/^cookie:\s*/i, '')
    .split(';')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const i = pair.indexOf('=');
      return i > 0 ? fromObject({ name: pair.slice(0, i), value: pair.slice(i + 1), domain: host, secure: siteUrl.startsWith('https:') }) : null;
    })
    .filter((c): c is AccessCookie => c !== null);
}

/** True when a cookie for `domain` belongs to the site (its domain or subdomains) or to an authentication service. */
export function cookieDomainAllowed(domain: string, siteUrl: string): boolean {
  const host = domain.toLowerCase().replace(/^\./, '');
  const site = siteDomain(siteUrl);
  if (!site || !host) return false;
  return hostMatches(host, site) || isAuthServiceHost(host);
}

/**
 * Reads cookies exported from a browser: a JSON array (browser extensions), a Playwright storage state
 * (`{ cookies: [...] }`), Netscape cookies.txt, or a `name=value; …` header. Keeps only the site's cookies and
 * the authentication services', still valid; tells which were left out and why.
 */
export function parseCookieImport(text: string, siteUrl: string, now: Date = new Date()): CookieImport {
  const trimmed = text.trim();
  let parsed: AccessCookie[] = [];
  let invalid = 0;
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    let json: unknown;
    try {
      json = JSON.parse(trimmed);
    } catch {
      return { accepted: [], rejected: [{ name: '', domain: '', reason: 'invalid' }] };
    }
    const list = Array.isArray(json)
      ? json
      : json && typeof json === 'object' && Array.isArray((json as { cookies?: unknown }).cookies)
        ? (json as { cookies: unknown[] }).cookies
        : [];
    for (const item of list) {
      const c = item && typeof item === 'object' ? fromObject(item as Record<string, unknown>) : null;
      if (c) parsed.push(c);
      else invalid++;
    }
  } else if (trimmed.includes('\t')) {
    parsed = fromNetscape(trimmed);
  } else {
    parsed = fromHeader(trimmed, siteUrl);
  }
  const nowS = now.getTime() / 1000;
  const accepted: AccessCookie[] = [];
  const rejected: RejectedCookie[] = invalid > 0 ? [{ name: '', domain: '', reason: 'invalid' }] : [];
  const seen = new Set<string>();
  for (const c of parsed) {
    if (!cookieDomainAllowed(c.domain, siteUrl)) rejected.push({ name: c.name, domain: c.domain, reason: 'other_site' });
    else if (c.expires !== -1 && c.expires <= nowS) rejected.push({ name: c.name, domain: c.domain, reason: 'expired' });
    else {
      const key = `${c.name}|${c.domain}|${c.path}`;
      if (seen.has(key) || accepted.length >= MAX_COOKIES) continue;
      seen.add(key);
      accepted.push(c);
    }
  }
  return { accepted, rejected };
}

export function summarizeCookies(cookies: AccessCookie[]): AccessCookieSummary[] {
  return cookies.map((c) => ({ name: c.name, domain: c.domain, expiresAt: c.expires === -1 ? null : new Date(c.expires * 1000).toISOString() }));
}

const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .refine(
    (v) => {
      try {
        const u = new URL(v);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'Use a full URL like https://www.example.com/sign-in' },
  );

const confirmed = z.literal(true, { error: 'Confirm that this is a test account you may use on this site.' });

/** Body of `PUT /projects/:id/access`. */
export const saveAccessSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('credentials'),
    /** The sign-in page; missing: wherever the visitor meets the sign-in form. */
    loginUrl: httpUrl.optional(),
    username: z.string().trim().min(1, 'Enter the email or username of the test account').max(320),
    password: z.string().min(1, 'Enter the password of the test account').max(500),
    confirm: confirmed,
  }),
  z.object({
    kind: z.literal('cookies'),
    /** The export as pasted or read from a file; the server keeps only the site's valid cookies. */
    cookies: z.string().min(2, 'Paste the exported cookies').max(200_000),
    confirm: confirmed,
  }),
]);
export type SaveAccessInput = z.input<typeof saveAccessSchema>;

/** A saved access as the dashboard sees it: what it is and whether it works, never the password or cookie values. */
export interface ProjectAccessDto {
  kind: AccessKind;
  status: AccessStatus;
  /** Why the last visit could not use it, in plain words. */
  statusReason: string | null;
  loginUrl: string | null;
  /** The account, partly hidden: `te•••@pullo.io`. */
  account: string | null;
  cookies: AccessCookieSummary[];
  /** Cookies left out at the import (other sites, expired). */
  rejected: RejectedCookie[];
  updatedAt: string;
}

/** Shows enough of an account name to recognise it: `test@pullo.io` → `te•••@pullo.io`. */
export function maskAccount(username: string): string {
  const at = username.indexOf('@');
  const local = at >= 0 ? username.slice(0, at) : username;
  const rest = at >= 0 ? username.slice(at) : '';
  return `${local.slice(0, Math.min(2, Math.max(1, local.length - 1)))}•••${rest}`;
}

/** The secrets of an access, as stored encrypted and handed to the runner of one of the project's visits. */
export type AccessSecret = { kind: 'credentials'; loginUrl: string | null; username: string; password: string } | { kind: 'cookies'; cookies: AccessCookie[] };

/** What a runner gets: the secret and its version (a newer version is a new access to try). */
export interface VisitAccessDto {
  version: number;
  secret: AccessSecret;
}

/** Answer to a runner waiting at a sign-in page (`POST /internal/visits/:id/access/wait`). */
export type VisitAccessStateDto = { state: 'pending' } | { state: 'declined' } | { state: 'provided'; access: VisitAccessDto };

export const accessWaitSchema = z.object({
  /** Version of the access the runner already tried; null: it had none. */
  tried: z.number().int().nullable().default(null),
  url: z.string().max(4096).default(''),
});

/** Body of `POST /internal/visits/:id/access/result`: how the sign-in with an access went. */
export const accessResultSchema = z.object({
  version: z.number().int(),
  ok: z.boolean(),
  /** `failed`: the form did not let the account in; `expired`: the cookies no longer sign in. */
  status: z.enum(['failed', 'expired']).optional(),
  reason: z.string().max(300).nullable().default(null),
});

/** Body of `POST /projects/:id/synthetic/tests/:testId/access`: the person's answer to the test's question. */
export const accessDecisionSchema = z.object({ decision: z.enum(['decline', 'provided']) });

/** The test's sign-in situation, for the panel on the running test. */
export interface TestAccessDto {
  mode: TestAccessMode;
  /** The project has a saved access (any status). */
  saved: boolean;
  request: AccessRequestState | null;
  /** Visitors waiting at a sign-in page right now. */
  waiting: number;
  /** Visitors that ended at a sign-in page. */
  loginWalls: number;
}
